import { Mail, Plus, Clock, Users, Send, BarChart3, Sparkles, FileEdit, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { OnboardingEmptyState } from '@/client/components/ui/empty-state';

export function EmailNewsletterPage() {
  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-rose-500/20 via-pink-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-pink-500/20 via-rose-500/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/20">
              <Mail className="size-5 text-white" />
            </div>
            Email Newsletter
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Send beautiful email campaigns to your subscribers
          </p>
        </div>
        <Button
          onClick={() => toast.info('Email Newsletter coming soon!')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700"
        >
          <Plus className="size-5" />
          Create Campaign
        </Button>
      </div>

      {/* Empty State */}
      <OnboardingEmptyState
        icon={Mail}
        title="Start your first campaign"
        steps={[
          { icon: Users, text: 'Build your subscriber list from forms and events' },
          { icon: FileEdit, text: 'Design beautiful emails with our drag-and-drop builder' },
          { icon: Send, text: 'Send campaigns and track opens and clicks' },
        ]}
        action={{
          label: 'Create your first campaign',
          onClick: () => toast.info('Email Newsletter coming soon!'),
          icon: Plus,
        }}
      />

      {/* Coming Soon Banner */}
      <Card className="rounded-2xl border-2 border-dashed border-rose-300 dark:border-rose-800 bg-gradient-to-r from-rose-50/50 to-pink-50/50 dark:from-rose-950/20 dark:to-pink-950/20">
        <CardContent className="flex flex-col sm:flex-row items-center gap-4 p-6">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
            <Clock className="size-8 text-white" />
          </div>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-lg font-semibold mb-1">Email Newsletter Coming Soon</h3>
            <p className="text-sm text-muted-foreground">
              We&apos;re building a powerful email marketing tool with a drag-and-drop builder,
              subscriber management, A/B testing, and detailed analytics. Your subscriber lists will
              automatically grow from form submissions and event signups.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Feature Preview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/20">
              <FileEdit className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Drag & Drop</p>
              <p className="text-lg font-semibold">Email Builder</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Sparkles className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">A/B Testing</p>
              <p className="text-lg font-semibold">Subject Lines</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <BarChart3 className="size-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-medium">Open & Click</p>
              <p className="text-lg font-semibold">Analytics</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Integration Note */}
      <Card className="rounded-2xl border-0 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="flex items-start gap-4 p-6">
          <div className="size-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
            <Zap className="size-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold mb-1">Integrated with your other tools</h4>
            <p className="text-sm text-muted-foreground">
              Email Newsletter will automatically sync with your Forms, Pages, and Links. Collect
              subscribers from event registrations, grow your list from link clicks, and track which
              campaigns drive the most engagement.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
